import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as FileCopyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  PlayArrow as UseIcon,
  History as VersionIcon,
  Visibility as ViewIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import ItemRenderer from "../items/ItemRenderer";
import ConfirmationDialog from "../common/ConfirmationDialog";
import {
  getTemplateDisplayName,
  getTemplateStats,
  formatTemplateForDisplay,
} from "../../models/Template";

/**
 * Enhanced template detail component with manager features
 *
 * @param {Object} props
 * @param {Object} props.template - Template data using new Template model
 * @param {Function} props.onEdit - Callback when edit button is clicked
 * @param {Function} props.onDelete - Callback when delete button is clicked
 * @param {Function} props.onDuplicate - Callback when duplicate button is clicked
 * @param {Function} props.onUse - Callback when use template button is clicked
 * @param {Function} props.onToggleFavorite - Callback when favorite button is clicked
 * @param {Function} props.onShare - Callback when share button is clicked
 */
const TemplateDetail = ({
  template,
  onEdit,
  onDelete,
  onDuplicate,
  onUse,
  onToggleFavorite,
  onShare,
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!template) {
    return null;
  }

  const displayData = formatTemplateForDisplay(template);
  const stats = getTemplateStats(template);

  const getCategoryColor = (category) => {
    const colorMap = {
      general: "default",
      safety: "error",
      maintenance: "success",
      inspection: "primary",
      quality: "info",
      compliance: "secondary",
      training: "warning",
      audit: "warning",
    };
    return colorMap[category] || "default";
  };

  const handleDeleteClick = () => {
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    onDelete(template.id);
    setConfirmDialogOpen(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexDirection: isMobile ? "column" : "row",
            mb: 3,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {displayData.category.length > 0 && (
                <Chip
                  label={
                    displayData.category.charAt(0).toUpperCase() +
                    displayData.category.slice(1)
                  }
                  color={getCategoryColor(displayData.category)}
                  variant="outlined"
                />
              )}
              <Chip
                label={`v${displayData.version}`}
                color="default"
                variant="outlined"
                size="small"
                icon={<VersionIcon />}
              />
              <Chip
                label={`ID: ${displayData.id}`}
                color="default"
                variant="outlined"
                size="small"
                icon={<VersionIcon />}
              />

              {!template.isActive && (
                <Chip
                  label="Inactive"
                  color="error"
                  variant="outlined"
                  size="small"
                />
              )}

              {/* <IconButton
                size="small"
                onClick={() => onToggleFavorite(template.id)}
                color="warning"
              >
                {template.isFavorite ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>*/}
            </Box>

            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              {getTemplateDisplayName(template)}
            </Typography>

            {template.description && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 2, lineHeight: 1.6 }}
              >
                {template.description}
              </Typography>
            )}

            {/* Tags */}
            {displayData.tags.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {displayData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Box>
            )}

            {/* Metadata */}
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">
                Created: {formatDate(template.createdAt)}
              </Typography>
              <Typography variant="body2">
                Updated: {formatDate(template.updatedAt)}
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: isMobile ? "flex-start" : "flex-end",
              width: isMobile ? "100%" : "auto",
              mt: isMobile ? 2 : 0,
            }}
          >
            {/* <Button
              variant="contained"
              color="primary"
              onClick={() => onUse(template.id)}
              size={isMobile ? "small" : "medium"}
              startIcon={<UseIcon />}
              fullWidth={isMobile}
            >
              Use Template
            </Button> */}
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onEdit(template.id)}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileCopyIcon />}
              onClick={() => onDuplicate(template.id)}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Duplicate
            </Button>
            {/* {onShare && (
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={() => onShare(template.id)}
                size={isMobile ? "small" : "medium"}
                fullWidth={isMobile}
              >
                Share
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Delete
            </Button> */}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Template Items Section */}
        <Typography variant="h6" component="h2" gutterBottom>
          Template Items ({template.items.length})
        </Typography>

        {template.items.length > 0 ? (
          <Box>
            {template.items
              .sort((a, b) => a.order - b.order)
              .map((item, index) => (
                <Box key={item.id} sx={{ mb: 1 }}>
                  <ItemRenderer
                    item={item}
                    onUpdate={() => {}}
                    onDelete={() => {}}
                    draggable={false}
                    editable={false}
                    showOrder={true}
                    orderNumber={index + 1}
                    isTemplateView={true}
                  />
                </Box>
              ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic", textAlign: "center", py: 4 }}
          >
            No items in this template.
          </Typography>
        )}
      </Paper>

      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Template"
        message={`Are you sure you want to delete "${getTemplateDisplayName(
          template
        )}"? This action cannot be undone and will affect any checklists using this template.`}
        confirmText="Delete"
        confirmColor="error"
      />
    </>
  );
};

export default TemplateDetail;
